import * as React from 'react'
import { Route, Switch } from 'react-router-dom'
import { bindActionCreators, Dispatch } from 'redux'
import { connect } from 'react-redux'
import { RouteComponentProps } from 'react-router-dom'
import { withRouter } from 'react-router-dom'
import { State } from 'store'
import { Account } from 'auth/state'
import { changeActiveAccount } from 'preferences/actions.async'
import { parseSearch, renderSearch } from 'navigation'
import { Combobox } from 'uiComponents/combobox/combobox'
import { useSingleAccountPreview } from 'header/singleAccountPreview'
import { Building2 } from 'lucide-react'
import classNames from 'classnames'
import { t } from 'translations'

interface SelectorDropdownProps {
    activeAccount: string
    accounts: Account[]
    changeActiveAccount: typeof changeActiveAccount
    selfSignUp: string | null
    previewSingleAccount: boolean
    collapsed: boolean
}

interface SelectorDropdownState {
    loading: boolean
    search: string
    railOpen: boolean
}

class SelectorDropdown extends React.Component<
    SelectorDropdownProps & RouteComponentProps<any>,
    SelectorDropdownState
> {
    popup: HTMLDivElement | null = null
    railPopover = React.createRef<HTMLDivElement>()

    constructor(props: SelectorDropdownProps & RouteComponentProps<any>) {
        super(props)
        this.state = {
            loading: false,
            search: '',
            railOpen: false,
        }
    }

    componentDidMount() {
        const urlSlug = this.props.match.params['accountSlug']
        if (urlSlug && urlSlug !== this.props.activeAccount && this.props.accounts.find((x) => x.slug === urlSlug)) {
            this.props.changeActiveAccount(urlSlug)
        }
    }

    componentDidUpdate(prevProps: SelectorDropdownProps & RouteComponentProps<any>) {
        const oldUrlSlug = prevProps.match.params['accountSlug']
        const newUrlsSlug = this.props.match.params['accountSlug']
        if (
            oldUrlSlug !== newUrlsSlug &&
            newUrlsSlug !== this.props.activeAccount &&
            this.props.accounts.find((x) => x.slug === newUrlsSlug)
        ) {
            this.props.changeActiveAccount(newUrlsSlug)
        }
    }

    openRail = () => {
        this.setState({ railOpen: true }, () => this.railPopover.current?.querySelector('input')?.focus())
    }

    closeRail = () => {
        this.setState({ railOpen: false })
    }

    setPopupRef = (node: HTMLDivElement) => {
        this.popup = node
    }

    onChange = async (slug: string) => {
        this.closeRail()
        if (this.props.activeAccount === slug && this.props.match.params['accountSlug'] === slug) {
            return
        }
        this.setState({ loading: true })
        await this.props.changeActiveAccount(slug)
        this.setState({ loading: false })
        const path = this.props.match.params[1]
        const search = this.props.location.search
        const queryItems = parseSearch(search)
        if (queryItems.page) {
            queryItems.page = '1'
        }
        const nonPersistentQueryItems = ['q', 'search', 'filter', 'checkout_funnel_rates', 'sankeyFilter']
        nonPersistentQueryItems.forEach((item) => {
            if (queryItems[item]) {
                delete queryItems[item]
            }
        })
        const query = renderSearch(queryItems)
        if (this.props.match.path.indexOf(':accountSlug') > -1) {
            this.props.history.push(`/account/${slug}/${path}${query}`)
        } else {
            this.props.history.push(`${this.props.location.pathname}${query}`)
        }
    }

    render() {
        const { accounts, activeAccount, previewSingleAccount } = this.props
        const inaccessibleAccount = this.props.match.params['accountSlug']
            ? this.props.match.params['accountSlug'] !== activeAccount
            : false
        const singleAccount = previewSingleAccount
            ? accounts.find((account) => account.slug === activeAccount) ?? accounts[0]
            : accounts.length === 1 && !inaccessibleAccount
            ? accounts[0]
            : null
        const sortedAccounts = accounts
            .map((x) => ({ label: x.name, value: x.slug }))
            .slice()
            .sort((a, b) =>
                a.label.toUpperCase() > b.label.toUpperCase()
                    ? 1
                    : b.label.toUpperCase() > a.label.toUpperCase()
                    ? -1
                    : 0,
            )

        const combobox = (open?: boolean) => (
            <Combobox
                options={sortedAccounts}
                value={activeAccount}
                onChange={this.onChange}
                disableClearable
                disablePortal
                loading={this.state.loading}
                {...(open ? { open: true, onClose: this.closeRail } : {})}
                slotProps={{
                    popper: {
                        placement: 'top-start',
                        popperOptions: { strategy: 'fixed' },
                        modifiers: [{ name: 'flip', enabled: false }],
                    },
                }}
            />
        )
        const accountName = singleAccount?.name ?? accounts.find((account) => account.slug === activeAccount)?.name

        if (this.props.collapsed) {
            return (
                <div className={classNames('account-rail', { open: this.state.railOpen })}>
                    <button
                        type="button"
                        className={classNames('account-rail-button', { static: !!singleAccount })}
                        aria-label={accountName}
                        aria-haspopup={singleAccount ? undefined : 'listbox'}
                        aria-expanded={singleAccount ? undefined : this.state.railOpen}
                        onClick={singleAccount ? undefined : this.openRail}
                    >
                        <Building2 size={16} aria-hidden />
                    </button>
                    <span className="account-rail-tooltip" role="tooltip">
                        <span className="account-rail-tooltip-name notranslate">{accountName}</span>
                        {!singleAccount && (
                            <span className="account-rail-tooltip-hint">{t('sidebar.switchAccount')}</span>
                        )}
                    </span>
                    {this.state.railOpen && (
                        <div className="account-rail-popover" ref={this.railPopover}>
                            {combobox(true)}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <div className="safari-wrapper">
                {singleAccount ? (
                    <div className="account-name">
                        <span className="account-name-text notranslate" title={singleAccount.name}>
                            {singleAccount.name}
                        </span>
                    </div>
                ) : (
                    combobox()
                )}
            </div>
        )
    }
}

function mapStateToProps(state: State) {
    return {
        accounts: state.auth.user ? state.auth.user.accounts : [],
        activeAccount: state.preferences.activeAccount,
    }
}

function mapDispatchToProps(dispatch: Dispatch) {
    return bindActionCreators(
        {
            changeActiveAccount,
        },
        dispatch,
    )
}

const ConnectedSelectorDropdown = withRouter(connect(mapStateToProps, mapDispatchToProps)(SelectorDropdown))

export function AccountSelector(props: { selfSignUp: string | null; collapsed?: boolean }) {
    const previewSingleAccount = useSingleAccountPreview()
    const renderDropdown = () => (
        <ConnectedSelectorDropdown
            selfSignUp={props.selfSignUp}
            previewSingleAccount={previewSingleAccount}
            collapsed={!!props.collapsed}
        />
    )
    return (
        <Switch>
            <Route path="/(account|widget)/:accountSlug/*" render={renderDropdown} />
            <Route render={renderDropdown} />
        </Switch>
    )
}
